'use strict';

require('../extensions');
const {floatsEqual, polygonName, sanitizeDegrees} = require('../utils/misc');

const Fitter = require('./Fitter');
const Point = require('./Point');
const Transformation = require('./Transformation');

function Shape(numSides) {
    const args = [].slice.call(arguments);
    this.numSides = args.shift();
    args.forEach((arg) => {
        if (arg && arg.isAPoint) {
            this.initialPoint = arg;
        } else if (arg && arg.length) {
            this.setPoints(arg);
        } else if (arg && arg > 0) {
            this.sideLength = arg;
        }
    });
    if (this.sideLength && !this.points && this.initialPoint) {
        this.generatePoints();
    }
    if (!this.sideLength && this.points && this.points.length > 1) {
        let length;
        this.getSideSegments().forEach(line => {
            const l = line.length();
            if (length === void 0) {
                length = l;
            } else if (!floatsEqual(length, l)) {
                length = false;
            }
        });
        if (length) {
            this.sideLength = length;
        }
    }
}

Object.assign(Shape.prototype, {
    useCorners: true,
    initialPoint: Point.origin(),
    dotSize: 0.1,
    regular: true,
    rotation: 0,
    clearColor: '#FFFFFF',
    strokeStyle: '#000000',
    fillStyle: '#000000',
    highlightStyle: '#FFFF00',
    lineWidth: 1,
    highlightMargin: 2,
    isAShape: true,
});

Shape.polygonName = function (sides) {
    return polygonName(sides);
};

Shape.prototype.log = function (what) {
    //console.log(what);
};

Shape.prototype.toString = function () {
    let shape = Shape.polygonName(this.points.length);
    if (this.getSideLength()) {
        shape = `${shape}<${this.sideLength.toFixed(2)}>`;
    }
    if (this.points.length <= 4) {
        shape = `${shape}[${this.points.join(', ')}]`;
    }
    return shape;
};

Shape.prototype._cloneFrom = function (other) {
    //
};

Shape.prototype.clone = function (cloningForFractals) {
    const s = new Shape(this.numSides, this.points);
    s.rotation = this.rotation;
    if (cloningForFractals) {
        this.getProportion();
        this.getCentralShapeFractalProportion();
        //this.getFractalGridProportion();
    }
    s._fractalGridProportion = this._fractalGridProportion;
    s._centralShapeFractalProportion = this._centralShapeFractalProportion;
    s._proportion = this._proportion;
    s._cloneFrom(this);
    return s;
};

Shape.prototype.getPoints = function () {
    return this.points;
};

Shape.prototype.getBoundingBox = function () {
    if (!this.boundingBox) {
        this.boundingBox = new BoundingBox(this);
    }
    return this.boundingBox;
};

Shape.prototype.getWidth = function () {
    return this.getBoundingBox().width;
};

Shape.prototype.getHeight = function () {
    return this.getBoundingBox().height;
};

Shape.prototype._clearCache = function () {
    this.sideSegments = void 0;
    this.anchorPoint = void 0;
    this.sidePoints = void 0;
    this.boundingBox = void 0;
    this.sideLength = void 0;
};

Shape.prototype.getSideLength = function () {
    if (!this.sideLength && this.points && this.points.length > 1) {
        this.sideLength = this.points[0].distance(this.points[1]);
    }
    return this.sideLength;
};

Shape.prototype.setRotation = function (theta) {
    let formerRotation = this.rotation;
    const rotationDelta = sanitizeDegrees(theta) - formerRotation;
    if (Math.abs(rotationDelta) > 0) {
        this.rotate(rotationDelta);
    }
};

Shape.prototype.rotate = function (theta) {
    theta = sanitizeDegrees(theta);
    this.rotation = sanitizeDegrees(this.rotation + theta);
    const center = this.getCenter();
    this._clearCache();
    this.points = this.points.map((p) => {
        const distance = center.distance(p);
        const angle = center.angleTo(p);
        const rotated = center.radialPoint(distance, theta + angle);
        return rotated;
    });
};

Shape.prototype.getSideSegments = function () {
    if (!this.sideSegments) {
        this.sideSegments = [];
        for (let i = 0; i < this.numSides; i++) {
            const a = this.points[i];
            const b = this.points[(i + 1) % this.points.length];
            this.sideSegments.push(new LineSegment(a, b));
        }
    }
    return this.sideSegments;
};

Shape.prototype.intersectsWith = function (otherShape) {
    const mySides = this.getSideSegments();
    const theirSides = otherShape.getSideSegments();
    for (let i = 0; i < mySides.length; i++) {
        for(let j = 0; j < theirSides.length; j++) {
            if (mySides[i].intersectsWith(theirSides[j])) {
                return true;
            }
        }
    }
    return false;
};

Shape.prototype.generatePoints = function () {
    const points = [this.initialPoint];

    const angleSum = 180 * (this.numSides - 2);
    const vertexAngle = angleSum / this.numSides * -1;
    let lastPoint = points[0];
    for (let i = 1; i < this.numSides; i++) {
        let nextPoint = lastPoint.radialPoint(this.getSideLength(), i * (180 - vertexAngle));
        points.push(nextPoint);
        lastPoint = nextPoint;
    }
    this.setPoints(points);
    return this.points;
};

Shape.prototype.setPoints = function (points) {
    this.points = points;
};

Shape.prototype.getAnchorPoint = function () {
    if (!this.anchorPoint) {
        this.anchorPoint = (new BoundingBox(this)).anchorPoint;
    }
    return this.anchorPoint;
};

Shape.prototype.scaleTo = function (proportion) {
    let anchorPoint = this.getAnchorPoint();
    this.points = this.points.map((point) => {
        return new Point(point.x * proportion, point.y * proportion);
    });
    this._clearCache();
    this.setAnchorPoint(anchorPoint);
};

Shape.prototype.scaleToFitWithin = function (width, height, permitFullRotation) {
    height = height || width;
    const bb = new BoundingBox(this);
    let scaleRatio;
    if (permitFullRotation) {
        const constrainedDimension = Math.min(width, height);
        scaleRatio = constrainedDimension / (bb.longestRadius * 2);
    } else {
        const widthRatio = width / bb.width;
        const heightRatio = height / bb.height;
        scaleRatio = Math.min(widthRatio, heightRatio);
    }
    this.scaleTo(scaleRatio);
};

Shape.prototype.centerAgainst = function (other) {
    if (other.isAShape) {
        const otherShape = other;
        const xD = (otherShape.getWidth() - this.getWidth()) / 2;
        const yD = (otherShape.getHeight() - this.getHeight()) / 2;
        this.setAnchorPoint(otherShape.getAnchorPoint().translate(xD, yD));
    } else if (other.isAPoint) {
        const diff = this.getCenter().diff(other);
        this.translate(diff);
    } else {
        console.error(`How do I center against ${other}???`);
    }
};

Shape.prototype.translate = function (xD, yD) {
    if (xD && xD.isAPoint) {
        this.points = this.points.map(p => p.translate(xD));
    } else {
        this.points = this.points.map((p) => p.translate(xD, yD));
    }
    this._clearCache();
};

Shape.prototype.alignPointTo = function (myPointIndex, otherPoint) {
    if (myPointIndex >= this.points.length) {
        throw new Error(`Bad point index: ${myPointIndex}`);
    }
    const myPoint = this.points[myPointIndex];
    const [xD, yD] = myPoint.delta(otherPoint);
    this.translate(xD, yD);
};

Shape.prototype.setAnchorPoint = function (anchorPoint) {
    let myPoint = this.getAnchorPoint();
    let xD = anchorPoint.x - myPoint.x;
    let yD = anchorPoint.y - myPoint.y;
    this.points = this.points.map(p => {
        return new Point(p.x + xD, p.y + yD);
    });
    this._clearCache();
    this.anchorPoint = anchorPoint;
};

Shape.prototype.draw = function (ctx, xform) {
    xform = xform || Transformation.IDENTITY;
    ctx.strokeStyle = this.strokeStyle;
    ctx.lineWidth = Math.max(0.1, this.lineWidth * xform.getZoom());
    const points = this.points.map(p => xform.translatePoint(p));
    ctx.beginPath();
    ctx.moveToPoint(points[0]);
    for (let i = 1; i < this.points.length; i++) {
        ctx.lineToPoint(points[i]);
    }
    ctx.lineToPoint(points[0]);
    ctx.stroke();
    if (this.boldPoints) {
        let size = 2;
        points.forEach((point) => {
            ctx.dot(point, size);
            if (this.numberedPoints) {
                size++;
            }
        });
    }
};

Shape.prototype.highlight = function (ctx, color) {
    ctx.strokeStyle = color ||  this.highlightStyle;
    ctx.lineWidth = this.lineWidth + this.highlightMargin * 2;
    ctx.beginPath();
    ctx.moveToPoint(this.points[0]);
    for (let i = 1; i < this.points.length; i++) {
        ctx.lineToPoint(this.points[i]);
    }
    ctx.lineToPoint(this.points[0]);
    ctx.stroke();
    this.draw(ctx);
};

Shape.prototype.fill = function (ctx, color) {
    color = color || this.fillStyle;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveToPoint(this.points[this.points.length - 1]);
    this.points.forEach(p => ctx.lineToPoint(p));
    ctx.fill();
};

Shape.prototype.getCenter = function () {
    let x = 0;
    let y = 0;
    this.points.forEach((point) => {
        x += point.x;
        y += point.y;
    });
    return new Point(x / this.points.length, y / this.points.length);
};

Shape.prototype.setFractalProportion = function (proportion) {
    this._proportion = proportion;
};

Shape.prototype.getProportionalSubShapes = function (max, proportion) {
    const shapes = [];
    this.points.forEach((point, i) => {
        if (max && shapes.length >= max) return;
        const mini = this.clone(false);
        const prop = proportion || this.getProportion();
        mini.scaleTo(prop);
        mini.alignPointTo(i, point);
        shapes.push(mini);
    });
    return shapes;
};

Shape.prototype.getProportion = function (ctx) {
    if (!this._proportion) {
        if (this.points.length === 4) {
            this._proportion = this.dumbSquare ? 0.5 : 0.51;
        } else {
            const fitter = new Fitter(0.5);
            fitter.fitTo((proportion) => {
                this.miniOne = this.clone();
                this.miniTwo = this.clone();
                this.miniOne.scaleTo(proportion);
                this.miniOne.alignPointTo(0, this.points[0]);
                this.miniTwo.scaleTo(proportion);
                this.miniTwo.alignPointTo(1, this.points[1]);
                if (ctx) {
                    this.miniOne.draw(ctx);
                    this.miniTwo.draw(ctx);
                }
                if (this.miniOne.intersectsWith(this.miniTwo)) {
                    return 1;
                } else {
                    return -1;
                }
            });
            this._proportion = fitter.value;
            this.log(`${this} has a fractal proportion of ${fitter.value}`);
        }
    }
    return this._proportion;
};

Shape.prototype.getOptimalRotationForCentralShape = function () {
    if (this.points.length % 2 === 0) {
        //even
        return 360 / this.points.length / 2;
    } else {
        //odd
        return 180;
    }
};

Shape.prototype.getCentralShapeFractalProportion = function () {
    if (!this._centralShapeFractalProportion) {
        const [mini] = this.getProportionalSubShapes(1);
        const fitter = new Fitter(0.5);
        const centralRotation = this.getOptimalRotationForCentralShape();
        fitter.fitTo((proportion) => {
            const centralShape = this.clone();
            centralShape.scaleTo(proportion);
            centralShape.centerAgainst(this.getCenter());
            centralShape.rotate(centralRotation);
            if (mini.intersectsWith(centralShape)) {
                return 1;
            } else {
                return -1;
            }
        });
        this._centralShapeFractalProportion = fitter.value;
    }
    return this._centralShapeFractalProportion;
};

Shape.prototype.getFractalGridProportion = function (testShape) {
    if (!this._fractalGridProportion) {
        const fitter = new Fitter(0.5);
        console.log(`Fit against ${testShape}`);
        fitter.fitTo((proportion) => {
            const [mini] = this.getProportionalSubShapes(1, proportion);
            if (mini.intersectsWith(testShape)) {
                this.log(`${mini} intersects with ${testShape} @${proportion}`);
                return 1;
            } else {
                this.log(`${mini} does not intersect with ${testShape} @${proportion}`);
                return -1;
            }
        });
        console.log(`${this} grid proportion is ${fitter.value}`);
        this._fractalGridProportion = fitter.value;
    }
    return this._fractalGridProportion;
};

Shape.prototype.getFractalGrid = function () {
    if (!this.fractalGrid) {
        const centralShape = this.clone(true);
        centralShape.scaleTo(this.getCentralShapeFractalProportion());
        centralShape.centerAgainst(this.getCenter());
        centralShape.rotate(this.getOptimalRotationForCentralShape());
        this.fractalGrid = {
            edgeShapes: this.getProportionalSubShapes()
        };
        this.fractalGrid.centralShape = centralShape;
    }
    return this.fractalGrid;
};

Shape.prototype.carpet = function (ctx, iterations) {
    if (!iterations) {
        return;
    }
    this.fill(ctx, '#FF0');
    const grid = this.getFractalGrid();
    //this.draw(ctx);
    grid.centralShape.fill(ctx, this.clearColor);
    //grid.centralShape.draw(ctx);
    grid.edgeShapes.forEach((shape) => {
        shape.fill(ctx, '#FF0');
        shape.carpet(ctx, iterations - 1);
    });
    grid.edgeShapes.forEach((shape) => {
        //shape.draw(ctx);
    });
    grid.centralShape.fill(ctx, '#F00');//this.clearColor);
};

Shape.prototype.getPointsForFractify = function () {
    if (this.useCorners) {
        return this.points;
    }
    if (!this.sidePoints) {
        this.sidePoints = [];
        let lastPoint = this.points[0];
        for (let i = 1; i <= this.points.length; i++) {
            let point = this.points[i % this.points.length];
            this.sidePoints.push(lastPoint.averageWith(point));
            lastPoint = point;
        }
    }
    if (this.allPoints) {
        return this.points.concat(this.sidePoints);
    }
    return this.sidePoints;
};

Shape.prototype.getFractalIteration = function (ctx) {
    if (!this.fractalIteration) {
        let lastPoint = this.getCenter();
        this.i = 0;
        this.fractalIteration = () => {
            let point = this.getPointsForFractify().rand();
            const resultPoint = lastPoint.proportionateJump(point, this.getProportion());
            if (!this.silent) {
                ctx.dot(resultPoint, this.dotSize);
            }
            lastPoint = resultPoint;
            this.i++;
            return lastPoint;
        };
    }
    return this.fractalIteration;
};

Shape.prototype.tick = function (ctx) {
    return this.getFractalIteration(ctx)();
};

Shape.prototype.mkAnimationTick = function (destinationCenter, destinationRotation) {
    const originalCenter = this.getCenter();
    let originalRotation = this.rotation;
    if (destinationRotation - 180 > originalRotation) {
        //rotate the other way
        originalRotation += 360;
    }
    const rotationDelta = destinationRotation - originalRotation;
    const centerDelta = originalCenter.diff(destinationCenter);
    return (percent) => {
        this.setRotation(originalRotation + rotationDelta * percent);
        const newCenter = originalCenter.translate(centerDelta.x * percent, centerDelta.y * percent);
        this.centerAgainst(newCenter);
    };
};

Shape.prototype.fractify = function (ctx, limit) {
    if (this.fractalInterval) {
        return this.fractalInterval;
    }
    limit = limit || Math.pow(10, 5);
    const thunk = this.getFractalIteration(ctx);
    thunk();
    this.fractalInterval = setInterval(() => {
        thunk();
        thunk();
        thunk();
        thunk();
        thunk();
        thunk();
        thunk();
        thunk();
        if (this.i > limit) {
            this.stopFractal();
        }
    }, 1);
    return this.fractalInterval;
};

Shape.prototype.stopFractal = function () {
    if (this.fractalInterval) {
        clearInterval(this.fractalInterval);
        this.fractalInterval = null;
        this.i = 0;
        console.log(`Stopping ${this}`);
    }
};

Shape.prototype.numberPoints = function (ctx, padding) {
    this.points.forEach((point, i) => {
        point.annotate(ctx, i + 1, i * (-360 / this.numSides) + 30, padding || 15);
    });
};

function LineSegment(point1, point2) {
    Shape.call(this, 2, [point1, point2]);
}

LineSegment.prototype = new Shape();
Shape.LineSegment = LineSegment;

LineSegment.prototype.getSideSegments = function() {
    return [this];
};

LineSegment.prototype.toString = function () {
    return `LineSegment(${this.points[0]}, ${this.points[1]})`;
};

function counterClockwiseOrder (pointA, pointB, pointC) {
    return ((pointC.y - pointA.y) * (pointB.x - pointA.x)) > ((pointB.y - pointA.y) * (pointC.x - pointA.x));
}

LineSegment.prototype.intersectsWith = function (otherSegment) {
    const thisBox = new BoundingBox(this);
    const otherBox = new BoundingBox(otherSegment);
    if (thisBox.minX > otherBox.maxX ||
        thisBox.minY > otherBox.maxY ||
        thisBox.maxX < otherBox.minX ||
        thisBox.maxY < otherBox.minY) {
        return false;
    }
    //Ok, our bounding boxes overlap
    const intersection = counterClockwiseOrder(this.points[0], otherSegment.points[0], otherSegment.points[1]) !== counterClockwiseOrder(this.points[1], otherSegment.points[0], otherSegment.points[1]) &&
        //ccw(A,B,C) != ccw(A,B,D)
        counterClockwiseOrder(this.points[0], this.points[1], otherSegment.points[0]) !== counterClockwiseOrder(this.points[0], this.points[1], otherSegment.points[1]);
    if (intersection) {
        //console.log(`${this} intersects with ${otherSegment}`);
    }
    return intersection;
};

LineSegment.prototype.length = function () {
    return this.points[0].distance(this.points[1]);
};

function Quadrilateral(points) {
    Shape.call(this, 4, points);
}
Quadrilateral.prototype = new Shape();
Shape.Quadrilateral = Quadrilateral;

function BoundingBox(/* Shape[, Shape] | corners*/) {
    const args = [].slice.call(arguments);
    let points = [];
    args.forEach((arg) => {
        [].concat(arg).forEach((ar) => {
            if (!ar.getPoints) {
                console.error(ar);
            }
            points = points.concat(ar.getPoints());
        });
    });
    this.minX = Point.minX(points);
    this.minY = Point.minY(points);
    this.maxX = Point.maxX(points);
    this.maxY = Point.maxY(points);
    this.width = this.maxX - this.minX;
    this.height = this.maxY - this.minY;
    Quadrilateral.call(this, [
        new Point(this.minX, this.minY),
        new Point(this.maxX, this.minY),
        new Point(this.maxX, this.maxY),
        new Point(this.minX, this.maxY),
    ]);
    this.longestRadius = 0;
    this.center = this.getCenter();
    this.points.forEach(p => {
        this.longestRadius = Math.max(this.longestRadius, p.distance(this.center));
    });
    this.anchorPoint = this.points[0];
}
BoundingBox.prototype = new Quadrilateral();

Quadrilateral.BoundingBox = BoundingBox;

function Rectangle(topLeftPoint, width, height) {
    height = height || width;
    const points = [];
    const deltas = [[0, 0], [0, 1], [1, 1], [1, 0]];
    deltas.forEach(([dx, dy]) => {
        points.push(topLeftPoint.translate(width * dx, height * dy));
    });
    Quadrilateral.call(this, points);
}
Rectangle.prototype = new Quadrilateral();
Quadrilateral.Rectangle = Rectangle;

function Square(topLeftPoint, sideLength) {
    this.sideLength = sideLength;
    this.initialPoint = topLeftPoint;
    this.generatePoints();
    Quadrilateral.call(this, this.points);
}
Square.prototype = new Quadrilateral();

Square.prototype.scalePoint = function (point, intoSquare) {
    const basePoint = this.points[0];
    const ratio = intoSquare.sideLength / this.sideLength;

    const xDiff = (basePoint.x - point.x) * ratio;
    const xResult = intoSquare.points[0].x - xDiff;

    const yDiff = (basePoint.y - point.y) * ratio;
    const yResult = intoSquare.points[0].y - yDiff;

    return new Point(xResult, yResult);
};

Square.prototype.getFractalIteration = function () {
    let lastPoint = this.getCenter();
    return () => {
        let iterationSquare = this.getFractalGrid().edgeShapes.rand();
        const resultPoint = this.scalePoint(lastPoint, iterationSquare);
        ctx.dot(resultPoint);
        lastPoint = resultPoint;
    };
};

Quadrilateral.Square = Square;


module.exports = Shape;

